import { FC, useState } from 'react';
import { useHistory } from 'react-router';
import { useMutation } from '@apollo/client';

import { CreditCard } from '__generated__/GQL';
import Loader from 'components/Loader';
import useToast from 'components/ToastProvider/useToast';

import { UpdateCreditCardPinDocument } from './graphql/updateCreditCardPin.gql';
import locales from './locales';
import UpdatePinEdit from './UpdatePinEdit';
import UpdatePinEditConfirm from './UpdatePinEditConfirm';
import UpdatePinIntro from './UpdatePinIntro';

interface UpdatePinProps {
  creditCard: CreditCard;
}

enum Step {
  Intro,
  Edit,
  Confirm,
}

interface State {
  pin: string;
}

const UpdatePin: FC<UpdatePinProps> = ({ creditCard }) => {
  const { creditCardId } = creditCard;

  const [step, setStep] = useState(Step.Intro);
  const [sharedState, setSharedState] = useState<State>({ pin: '' });

  const { showToast } = useToast();
  const history = useHistory();

  const [updateCreditCardPin, { loading }] = useMutation(
    UpdateCreditCardPinDocument,
  );

  if (loading) {
    return <Loader />;
  }

  const handleSubmit = async (pin: string) => {
    if (pin !== sharedState.pin) {
      showToast({ message: locales.errorPinMatchMessage, type: 'error' });
      setStep(Step.Edit);
      return;
    }

    await updateCreditCardPin({
      variables: {
        input: {
          creditCardId,
          pin,
        },
      },
    });

    showToast({ message: locales.successMessage, type: 'success' });
    history.push(`/cards/${creditCard.creditCardId}`);
    setStep(Step.Intro);
  };

  switch (step) {
    default:
    case Step.Intro:
      return (
        <UpdatePinIntro
          creditCardId={creditCardId}
          onNext={() => setStep(Step.Edit)}
        />
      );
    case Step.Edit:
      return (
        <UpdatePinEdit
          creditCardId={creditCardId}
          onNext={(pin: string) => {
            setSharedState({ ...sharedState, pin });
            setStep(Step.Confirm);
          }}
        />
      );
    case Step.Confirm:
      return (
        <UpdatePinEditConfirm
          creditCardId={creditCardId}
          onSubmit={handleSubmit}
        />
      );
  }
};

export default UpdatePin;
